import React, { useState, useEffect } from 'react';
import { FaDownload, FaRedo, FaUmbrella } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const APYCalculator = () => {
  const [currentAge, setCurrentAge] = useState(25);
  const [desiredPension, setDesiredPension] = useState(5000);
  const [result, setResult] = useState(null);

  // APY Contribution Chart (Monthly contribution based on age and pension amount)
  const apyChart = {
    1000: {
      18: 42, 19: 46, 20: 50, 21: 54, 22: 59, 23: 64, 24: 70, 25: 76,
      26: 82, 27: 90, 28: 97, 29: 106, 30: 116, 31: 126, 32: 138, 33: 151,
      34: 165, 35: 181, 36: 198, 37: 218, 38: 240, 39: 264,
    },
    2000: {
      18: 84, 19: 92, 20: 100, 21: 108, 22: 117, 23: 127, 24: 139, 25: 151,
      26: 164, 27: 178, 28: 194, 29: 212, 30: 231, 31: 252, 32: 276, 33: 302,
      34: 330, 35: 362, 36: 396, 37: 436, 38: 480, 39: 528,
    },
    3000: {
      18: 126, 19: 138, 20: 150, 21: 162, 22: 177, 23: 192, 24: 208, 25: 226,
      26: 246, 27: 268, 28: 292, 29: 318, 30: 347, 31: 379, 32: 414, 33: 453,
      34: 495, 35: 543, 36: 594, 37: 654, 38: 720, 39: 792,
    },
    4000: {
      18: 168, 19: 183, 20: 198, 21: 215, 22: 234, 23: 254, 24: 277, 25: 301,
      26: 327, 27: 356, 28: 388, 29: 423, 30: 462, 31: 504, 32: 551, 33: 602,
      34: 659, 35: 722, 36: 792, 37: 870, 38: 957, 39: 1054,
    },
    5000: {
      18: 210, 19: 228, 20: 248, 21: 269, 22: 292, 23: 318, 24: 346, 25: 376,
      26: 409, 27: 446, 28: 485, 29: 529, 30: 577, 31: 630, 32: 689, 33: 752,
      34: 824, 35: 902, 36: 990, 37: 1087, 38: 1196, 39: 1318,
    },
  };

  const pensionOptions = [1000, 2000, 3000, 4000, 5000];

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [currentAge, desiredPension]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const age = Number(currentAge) || 0;
    
    if (age < 18 || age > 39) {
      setResult(null);
      return;
    }

    const monthlyContribution = apyChart[desiredPension]?.[age] || 0;
    const yearsToContribute = 60 - age;
    const totalContribution = monthlyContribution * 12 * yearsToContribute;
    
    // Corpus calculation (approximate, actual depends on returns)
    const corpusMultiplier = {
      1000: 1.7,
      2000: 3.4,
      3000: 5.1,
      4000: 6.8,
      5000: 8.5,
    };
    const estimatedCorpus = desiredPension * 1000 * (corpusMultiplier[desiredPension] || 1);
    
    // Spouse pension (same as subscriber)
    const spousePension = desiredPension;
    
    // Nominee gets corpus on death of both
    const nomineePayout = estimatedCorpus;

    setResult({
      monthlyContribution,
      annualContribution: monthlyContribution * 12,
      totalContribution,
      yearsToContribute,
      desiredPension,
      estimatedCorpus,
      spousePension,
      nomineePayout,
    });
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('Atal Pension Yojana Report', {
      'Current Age': `${currentAge} years`,
      'Desired Pension': formatCurrency(desiredPension) + '/month',
      'Monthly Contribution': formatCurrency(result.monthlyContribution),
      'Annual Contribution': formatCurrency(result.annualContribution),
      'Years to Contribute': `${result.yearsToContribute} years`,
      'Total Contribution': formatCurrency(result.totalContribution),
      'Pension at 60': formatCurrency(desiredPension) + '/month',
      'Spouse Pension': formatCurrency(result.spousePension) + '/month',
      'Estimated Corpus': formatCurrency(result.estimatedCorpus),
    });
  };

  const handleReset = () => {
    setCurrentAge(25);
    setDesiredPension(5000);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="APY Calculator"
        description="Free Atal Pension Yojana Calculator - Calculate APY pension based on age and contribution."
        keywords="APY calculator, Atal Pension Yojana calculator, APY pension calculator, APY contribution chart, government pension scheme"
        canonicalPath="/apy"
      />
      <div className="calculator-header">
        <h1><FaUmbrella style={{ color: '#14b8a6' }} /> Atal Pension Yojana (APY)</h1>
        <p>Calculate contributions for guaranteed pension scheme for unorganized sector</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Your Current Age (Years)</label>
            <input
              type="number"
              value={currentAge === '' ? '' : currentAge}
              onChange={(e) => setCurrentAge(e.target.value === '' ? '' : Number(e.target.value))}
              min="18"
              max="39"
            />
            <small style={{ color: 'var(--text-secondary)' }}>Eligible: 18-39 years</small>
            <div className="slider-container">
              <input
                type="range"
                min="18"
                max="39"
                value={currentAge || 18}
                onChange={(e) => setCurrentAge(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Desired Monthly Pension at Age 60</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginTop: '8px' }}>
              {pensionOptions.map((pension) => (
                <button
                  key={pension}
                  onClick={() => setDesiredPension(pension)}
                  style={{
                    padding: '12px 8px',
                    border: desiredPension === pension ? '2px solid #14b8a6' : '1px solid var(--border)',
                    borderRadius: '8px',
                    background: desiredPension === pension ? '#ccfbf1' : 'var(--surface)',
                    cursor: 'pointer',
                    fontWeight: desiredPension === pension ? 'bold' : 'normal',
                  }}
                >
                  ₹{pension.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #14b8a6, #0d9488)' }}>
                <h3>Monthly Contribution Required</h3>
                <div className="amount">{formatCurrency(result.monthlyContribution)}</div>
                <small>For ₹{desiredPension.toLocaleString()} pension at age 60</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#ecfdf5', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Monthly Pension</span>
                  <span className="value" style={{ color: '#059669', fontSize: '1.3em' }}>{formatCurrency(desiredPension)}</span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Contribution Period</span>
                  <span className="value" style={{ color: '#d97706', fontSize: '1.3em' }}>{result.yearsToContribute} years</span>
                </div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Annual Contribution</span>
                  <span className="value">{formatCurrency(result.annualContribution)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Contribution till 60</span>
                  <span className="value">{formatCurrency(result.totalContribution)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Spouse Pension (on death)</span>
                  <span className="value">{formatCurrency(result.spousePension)}/month</span>
                </div>
                <div className="result-item" style={{ background: '#eff6ff' }}>
                  <span className="label">Nominee Payout (on death of both)</span>
                  <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.estimatedCorpus)}</span>
                </div>
              </div>

              {/* Contribution Chart */}
              <div className="breakup-section">
                <h4>APY Contribution Chart (Monthly)</h4>
                <div style={{ overflowX: 'auto' }}>
                  <table className="tax-slab-table" style={{ fontSize: '11px' }}>
                    <thead>
                      <tr>
                        <th>Age</th>
                        <th>₹1000</th>
                        <th>₹2000</th>
                        <th>₹3000</th>
                        <th>₹4000</th>
                        <th>₹5000</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[18, 20, 25, 30, 35, 39].map((age) => (
                        <tr key={age} style={{ background: age === currentAge ? '#ccfbf1' : 'inherit' }}>
                          <td style={{ fontWeight: age === currentAge ? 'bold' : 'normal' }}>{age}</td>
                          {pensionOptions.map((p) => (
                            <td 
                              key={p}
                              style={{ 
                                fontWeight: age === currentAge && p === desiredPension ? 'bold' : 'normal',
                                background: age === currentAge && p === desiredPension ? '#14b8a6' : 'inherit',
                                color: age === currentAge && p === desiredPension ? 'white' : 'inherit',
                              }}
                            >
                              ₹{apyChart[p][age]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              <p>Please enter a valid age between 18-39 years</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 APY Key Features</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li>Guaranteed pension of ₹1000-5000/month</li>
              <li>Age eligibility: 18-39 years</li>
              <li>Contribution till age 60</li>
              <li>Pension starts from age 60</li>
              <li>Spouse gets same pension after subscriber's death</li>
            </ul>
          </div>
          <div>
            <ul>
              <li>Nominee gets corpus on death of both</li>
              <li>Government co-contribution for eligible subscribers</li>
              <li>Tax benefit under Section 80CCD</li>
              <li>Auto-debit from bank account</li>
              <li>Available through all banks</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APYCalculator;
